import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getUsers, userDownload } from '../../Services/serviceURL';
import { CustomerLite } from '../Plants/CreatePlant';
import { buildProfileTabConfig } from './profileTabConfig';
import type { ProfileData, ProfileSortConfig, SortableKeys } from './profileTypes';
import {
    extractCustomersFromUserApi,
    fetchProfileRows,
    mapApiUserToProfile,
    sortAndFilterProfiles,
} from './profileUsers';

const USER_TYPE_OPTIONS = ['Internal', 'External'];

export function useProfilePage() {
    const [sortConfig, setSortConfig] = useState<ProfileSortConfig>(null);
    const [customers, setCustomers] = useState<CustomerLite[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);
    const [isLoadingProfile, setIsLoadingProfile] = useState(true);
    const [allProfiles, setAllProfiles] = useState<ProfileData[]>([]);
    const [selectedCustomerId, setSelectedCustomerId] = useState<string>('');
    const [selectedUserType, setSelectedUserType] = useState<string>('All');
    const location = useLocation();
    const navigate = useNavigate();
    const [btnDisabled] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [isCustomerOpen, setIsCustomerOpen] = useState(false);
    const [customerSearch, setCustomerSearch] = useState('');
    const [isUserTypeOpen, setIsUserTypeOpen] = useState(false);
    const [userTypeSearch, setUserTypeSearch] = useState('');

    const tabConfig = useMemo(() => buildProfileTabConfig(), []);

    useEffect(() => {
        setIsLoadingProfile(true);
        getUsers('All')
            .then((res) => {
                const fetchedData = res.Data;
                if (!Array.isArray(fetchedData)) {
                    setAllProfiles([]);
                    return;
                }
                setAllProfiles(fetchedData.map((usr: Record<string, unknown>) => mapApiUserToProfile(usr)));
            })
            .catch(() => setAllProfiles([]))
            .finally(() => setIsLoadingProfile(false));
    }, []);

    useEffect(() => {
        const fetchCustomersFromUsers = async () => {
            try {
                const response = await getUsers('All');
                if (!response?.Data || !Array.isArray(response.Data)) {
                    setCustomers([]);
                    return;
                }
                setCustomers(extractCustomersFromUserApi(response.Data));
            } catch {
                setCustomers([]);
            }
        };
        fetchCustomersFromUsers();
    }, []);

    useEffect(() => {
        setCurrentPage(1);
    }, [allProfiles]);

    useEffect(() => {
        const previousBodyOverflow = document.body.style.overflow;
        const previousHtmlOverflow = document.documentElement.style.overflow;
        document.body.style.overflow = 'hidden';
        document.documentElement.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = previousBodyOverflow;
            document.documentElement.style.overflow = previousHtmlOverflow;
        };
    }, []);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as Element | null;
            if (!target?.closest('.searchable-select-containers')) {
                setIsCustomerOpen(false);
                setIsUserTypeOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        const state = location.state as { userDeleted?: boolean; message?: string } | null;
        if (!state?.userDeleted) {
            return;
        }
        toast.success(state.message || 'User deleted successfully');
        navigate(location.pathname, { replace: true, state: {} });
    }, [location.state, location.pathname, navigate]);

    const filteredCustomersList = useMemo(() => {
        const list = customers || [];
        const normalize = (s: unknown) => String(s ?? '').toLowerCase().replace(/\s+/g, ' ').trim();
        const term = normalize(customerSearch);
        if (!term) {
            return list;
        }
        return list.filter(c => {
            const name = normalize(c.customerName);
            const soldTo = normalize(c.soldTo);
            return name === term || soldTo === term || name.includes(term) || soldTo.includes(term);
        });
    }, [customers, customerSearch]);

    const filteredUserTypesList = useMemo(() => {
        if (!userTypeSearch.trim()) {
            return USER_TYPE_OPTIONS;
        }
        const term = userTypeSearch.toLowerCase();
        return USER_TYPE_OPTIONS.filter(type => type.toLowerCase().includes(term));
    }, [userTypeSearch]);

    const requestSort = (key: SortableKeys) => {
        const direction = sortConfig?.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
        setSortConfig({ key, direction });
    };

    const sortedAndFilteredProfiles = useMemo(
        () => sortAndFilterProfiles(allProfiles, searchText, sortConfig),
        [allProfiles, searchText, sortConfig]
    );

    const totalItems = sortedAndFilteredProfiles.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);

    const paginatedProfiles = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        return sortedAndFilteredProfiles.slice(startIndex, startIndex + itemsPerPage);
    }, [sortedAndFilteredProfiles, currentPage, itemsPerPage]);

    useEffect(() => {
        if (totalPages === 0 && currentPage !== 1) {
            setCurrentPage(1);
            return;
        }
        if (totalPages > 0 && currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [totalPages, currentPage]);

    const reloadProfiles = async (customerId: string, userType: string, resetSearch = false) => {
        setCurrentPage(1);
        if (resetSearch) {
            setSearchText('');
        }
        setIsLoadingProfile(true);
        try {
            const profiles = await fetchProfileRows(customerId, userType, customers);
            setAllProfiles(profiles);
        } catch {
            setAllProfiles([]);
        } finally {
            setIsLoadingProfile(false);
        }
    };

    const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
        setSearchText(e.target.value);
        setCurrentPage(1);
    };

    const handleCustomerSelect = (customerId: string) => {
        setSelectedCustomerId(customerId);
        setIsCustomerOpen(false);
        setCustomerSearch('');
        reloadProfiles(customerId, selectedUserType, true);
    };

    const handleUserTypeSelect = (userType: string) => {
        setSelectedUserType(userType);
        setIsUserTypeOpen(false);
        setUserTypeSearch('');
        reloadProfiles(selectedCustomerId, userType);
    };

    const toggleCustomerDropdown = () => {
        setIsCustomerOpen(prev => !prev);
        setIsUserTypeOpen(false);
    };

    const toggleUserTypeDropdown = () => {
        setIsUserTypeOpen(prev => !prev);
        setIsCustomerOpen(false);
    };

    const handleDownload = async () => {
        try {
            const response = await userDownload('All');
            const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            link.href = url;
            link.setAttribute('download', 'users_data.csv');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch {
            // ignore
        }
    };

    const handleProfileClick = (id: string) => {
        navigate(`/users/edit/${id}`);
    };

    const handlePreviousPage = () => {
        setCurrentPage(prev => Math.max(1, prev - 1));
    };

    const handleNextPage = () => {
        setCurrentPage(prev => (totalPages === 0 ? 1 : Math.min(totalPages, prev + 1)));
    };

    return {
        tabConfig,
        searchText,
        handleSearch,
        customers,
        selectedCustomerId,
        isCustomerOpen,
        customerSearch,
        filteredCustomersList,
        toggleCustomerDropdown,
        setCustomerSearch,
        handleCustomerSelect,
        selectedUserType,
        isUserTypeOpen,
        userTypeSearch,
        filteredUserTypesList,
        toggleUserTypeDropdown,
        setUserTypeSearch,
        handleUserTypeSelect,
        handleDownload,
        btnDisabled,
        handleAddUser: () => navigate('/users/add'),
        isLoadingProfile,
        paginatedProfiles,
        sortConfig,
        requestSort,
        handleProfileClick,
        currentPage,
        totalPages,
        totalItems,
        itemsPerPage,
        handlePreviousPage,
        handleNextPage,
    };
}
